import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HeatmapComponent } from './heatmap/heatmap.component';
import { FormsModule } from '@angular/forms';
import { TemperatureComponent } from './temperature/temperature.component';

import { Screen3Component } from './screen3/screen3.component';
import { GraphComponent } from './graph/graph.component';
import { CuringComponent } from './curing/curing.component';
import { SubviewsComponent } from './subviews/subviews.component';
import { HealthComponent } from './health/health.component';
import { ProjectComponent } from './project/project.component';
import { ProjectInformationComponent } from './project-information/project-information.component';
import { LogoutComponent } from './logout/logout.component';
import { NewComponent } from './new/new.component';

// HTTP modules required by MSAL
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';

// Required for MSAL
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalRedirectComponent,
} from '@azure/msal-angular';
import { LoginComponent } from './login/login.component';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { MainLayoutComponent } from './main-layout/main-layout.component';
import { ExportProjectComponent } from './export-project/export-project.component';
import { PreferencesComponent } from './settings/preferences.component';
import { AdminLayoutComponent } from './admin/admin-layout.component';
import { CustomersComponent } from './admin/customers/customers.component';
import { DevicesComponent } from './admin/devices/devices.component';
import { UsersComponent } from './admin/users/users.component';
import { CustomerSelectorComponent } from './customer-selector/customer-selector.component';
import { environment } from '../environments/environment';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      // 'Application (client) ID' of app registration in the Microsoft Entra admin center - this value is a GUID
      //clientId: '621f284b-5a6f-4091-846e-d849199ef97b',
      // My Tenant - Multi Personal
      //clientId: 'daad6de0-17d9-4925-bfaa-9f845007f085',
      // Vericure.io - Multi
      clientId: '7371635d-af32-439b-8a9a-0e6038e5c6b3',
      // Full directory URL, in the form of https://login.microsoftonline.com/<tenant>
      //authority: 'https://login.microsoftonline.com/83310ec5-e3da-4db5-a5f8-71d571cc6d02/',
      authority: 'https://login.microsoftonline.com/common/',
      // Must be the same redirectUri as what was provided in your app registration.
      redirectUri: environment.redirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE,
    },
  });
}

// MSAL Interceptor is required to request access tokens in order to access the protected resource (Graph)
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', [
    'User.Read',
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

// MSAL Guard is required to protect routes and require authentication before accessing protected routes
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['User.Read'],
    },
  };
}

@NgModule({
  declarations: [
    AppComponent,
    HeatmapComponent,
    TemperatureComponent,
    Screen3Component,
    GraphComponent,
    CuringComponent,
    SubviewsComponent,
    HealthComponent,
    ProjectComponent,
    ProjectInformationComponent,
    LogoutComponent,
    NewComponent,
    LoginComponent,
    UnauthorizedComponent,
    MainLayoutComponent,
    ExportProjectComponent,
    PreferencesComponent,
    AdminLayoutComponent,
    CustomersComponent,
    DevicesComponent,
    UsersComponent,
    CustomerSelectorComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    MsalModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
